import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { apiClient } from "../../lib/api";
import { trackFbEvent, trackThenNavigate, generateEventId } from "../../lib/fbpixel";

/**
 * Floating WhatsApp channel button — sticky bottom-right on all landing pages.
 * Fetches the channel URL from /api/config so it stays in sync with the env var.
 * Shows a small "peek" tooltip on first render (once per session) to invite the click.
 */
export default function WhatsAppFab() {
  const [href, setHref] = useState("");
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    apiClient
      .get("/config")
      .then((r) => setHref(r.data.whatsapp_url || ""))
      .catch(() => {});
    const seen = typeof window !== "undefined" && window.sessionStorage?.getItem("sircs_wa_tip");
    if (!seen) {
      const t = setTimeout(() => setShowTip(true), 3500);
      const hide = setTimeout(() => setShowTip(false), 12000);
      return () => {
        clearTimeout(t);
        clearTimeout(hide);
      };
    }
  }, []);

  const dismissTip = () => {
    setShowTip(false);
    if (typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.setItem("sircs_wa_tip", "1");
    }
  };

  const handleClick = (e) => {
    dismissTip();
    if (!href) return;
    // Fire pixel Contact first, wait 300ms so the request leaves the browser,
    // then open WhatsApp. Prevents mobile from killing the fbq call.
    e.preventDefault();
    const eventId = generateEventId();
    trackThenNavigate(
      () => {
        trackFbEvent(
          "Contact",
          { content_name: "WhatsApp Canal — FAB flotante" },
          eventId
        );
      },
      () => {
        try {
          window.location.href = href;
        } catch (_) {
          // no-op: nothing else we can safely do here
        }
      },
      300
    );
  };

  if (!href) return null;

  return (
    <div className="fixed z-[60] bottom-5 right-5 sm:bottom-7 sm:right-7 flex items-end gap-3 pointer-events-none">
      <AnimatePresence>
        {showTip && (
          <motion.div
            initial={{ opacity: 0, x: 10, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 10, scale: 0.96 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="relative mb-1 pointer-events-auto max-w-[220px] sm:max-w-[260px] rounded-lg bg-[#0B0E13] border border-[#C7AA78]/40 px-4 py-3 pr-8 shadow-[0_8px_24px_rgba(11,14,19,0.30)]"
            data-testid="whatsapp-fab-tip"
          >
            <p className="text-[11px] tracking-[0.20em] uppercase text-[#C7AA78] font-medium font-sans mb-1">
              Canal privado gratis
            </p>
            <p className="text-[#F2EFEA] text-[13px] leading-snug font-light font-sans">
              Contenido exclusivo de Santi y avisos de próximas sesiones.
            </p>
            <button
              onClick={dismissTip}
              className="absolute top-2 right-2 text-[#AEB4BC] hover:text-[#F2EFEA] transition-colors"
              aria-label="Cerrar aviso"
              data-testid="whatsapp-fab-tip-close"
            >
              <X size={14} />
            </button>
            <span className="absolute -bottom-1 right-6 h-2 w-2 rotate-45 bg-[#0B0E13] border-r border-b border-[#C7AA78]/40" />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut", delay: 1.2 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="pointer-events-auto relative flex items-center justify-center h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] shadow-[0_10px_30px_rgba(37,211,102,0.35)] transition-colors ring-1 ring-[#25D366]/40"
        aria-label="Únete al canal de WhatsApp"
        data-testid="whatsapp-fab-btn"
      >
        {/* Official WhatsApp glyph */}
        <svg viewBox="0 0 32 32" className="h-7 w-7 sm:h-8 sm:w-8 text-white" fill="currentColor" aria-hidden="true">
          <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.53 2.478-1.318.115-.244.115-.487.15-.746 0-.44-.257-.53-.61-.716-.16-.086-.343-.174-.53-.243l-.72-.302c-.171-.076-.343-.13-.514-.13z"/>
          <path d="M25.616 6.415C23.043 3.843 19.618 2.428 15.985 2.428c-7.475 0-13.55 6.076-13.55 13.55 0 2.393.63 4.712 1.83 6.775l-1.949 7.115 7.276-1.905c1.978.98 4.146 1.5 6.398 1.5h.006c7.469 0 13.545-6.076 13.545-13.55 0-3.632-1.416-7.055-3.925-9.5zm-9.632 20.845h-.005c-2.02 0-3.994-.545-5.717-1.573l-.407-.244-4.247 1.114 1.14-4.14-.267-.42a11.257 11.257 0 0 1-1.71-5.984c0-6.216 5.056-11.268 11.273-11.268 3.018 0 5.85 1.174 7.985 3.308 2.13 2.13 3.303 4.968 3.303 7.98-.005 6.217-5.06 11.267-11.348 11.267z"/>
        </svg>
        <span className="absolute inset-0 rounded-full animate-ping bg-[#25D366]/25" aria-hidden="true" />
      </motion.a>
    </div>
  );
}
